import React from 'react'
import classNames from 'classnames'

import type { AvatarSize } from './constants'
import { AVATAR_SIZE_MAP, getColorStyleForLabel } from './constants'

const BASE_CLASSNAMES =
  'font-weight-semibold background-cover background-center background-no-repeat shade-100 radius-round flexbox-inline flexbox-centered font-family-system flexbox-no-shrink'

const PLACEHOLDER_AVATAR_URL =
  'https://assets.slite.com/illustrations/avatar-placeholder.png'

const avatarLabelClassesFromSizeProp: Record<AvatarSize, string> = {
  xxsmall: 'font-size-smallest',
  xsmall: 'font-size-smallest',
  small: 'font-size-small',
  medium: 'font-size-small',
  large: 'font-size-small',
  huge: 'font-size-normal',
}

export type AvatarProps = {
  size?: AvatarSize
  style?: React.CSSProperties
  photoUrl?: string | null
  name?: string
  className?: string
  // For the AvatarGroup component we need to display +X in the avatar,
  // so we shortcut the name formatting
  formatName?: boolean
  disabled?: boolean
} & React.HTMLAttributes<HTMLDivElement> & { ref?: React.Ref<HTMLDivElement> }

export function Avatar({
  name,
  formatName = true,
  photoUrl,
  style,
  size = 'medium',
  className,
  disabled,
  children,
  ref,
  ...props
}: AvatarProps) {
  const trimmedName = name?.trim()
  const hasName = !!trimmedName
  // Fall back to a static placeholder image when we have neither a real name
  // (to derive initials from) nor a photo. Avoids the empty-bubble look.
  const effectivePhotoUrl =
    photoUrl || (!hasName ? PLACEHOLDER_AVATAR_URL : null)

  const _style = {
    height: AVATAR_SIZE_MAP[size],
    width: AVATAR_SIZE_MAP[size],
    ...(photoUrl && hasName ? null : getColorStyleForLabel(name || 'Unknown')),
    ...(effectivePhotoUrl && {
      backgroundImage: `url(${effectivePhotoUrl})`,
    }),
    ...style,
    ...(disabled && {
      filter: 'grayscale(100%)',
      opacity: 0.6,
    }),
  }

  const formattedName =
    hasName && formatName
      ? trimmedName
          .replace(/\s/g, '')
          .slice(0, ['xs', 'xxs'].includes(size) ? 1 : 2)
      : name

  return (
    <span
      className={classNames(BASE_CLASSNAMES, className)}
      style={_style}
      ref={ref}
      data-test-id="avatar"
      {...props}
    >
      <span
        className={classNames(
          'center-text font-weight-semibold line-height-small',
          avatarLabelClassesFromSizeProp[size],
          photoUrl ? 'transparent' : ''
        )}
      >
        {formattedName ? formattedName : children}
      </span>
    </span>
  )
}
